import { useState, useEffect, useRef } from "react";
import { getNoticeDetail } from "../../../utils/adminNoticeService";
import { apiPutFormData } from "../../../utils/apiClient";
import { formatDateTime } from "../../../utils/commonService";
import "./css/adminnoticedetail.css";
import { IoClose } from "../../../components/icons";


const AdminNoticeDetail = ({ noticeIdx, onClose, onUpdate }) => {
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedNotice, setEditedNotice] = useState({
    title: "",
    isPinned: false,
    noticeType: "공통",
  });
  const [descImgFiles, setDescImgFiles] = useState([]);
  const [allAttachFiles, setAllAttachFiles] = useState([]);
  const [deletedAttachFileIdxList, setDeletedAttachFileIdxList] = useState(
    [],
  );

  const descEditorRef = useRef(null);
  const descImgInputRef = useRef(null);
  const savedRangeRef = useRef(null);

  useEffect(() => {
    const fetchNoticeDetail = async () => {
      setLoading(true);
      const result = await getNoticeDetail(noticeIdx);
      if (result.success) {
        setNotice(result.data);
        setEditedNotice({
          title: result.data.title,
          isPinned: result.data.isPinned,
          noticeType: result.data.noticeType,
        });
      } else {
        alert(result.error || "공지사항을 불러오는데 실패했습니다.");
      }
      setLoading(false);
    };

    fetchNoticeDetail();
  }, [noticeIdx]);

  // 편집 모드 진입 시 에디터 초기화
  useEffect(() => {
    if (isEditMode && descEditorRef.current && notice) {
      descEditorRef.current.innerHTML = notice.content || "";
      setDescImgFiles([]);
      setAllAttachFiles(
        (notice.img?.files || []).map((f) => ({
          type: "existing",
          fileIdx: f.fileIdx,
          name: f.originalName,
          url: f.fileUrl,
        })),
      );
      setDeletedAttachFileIdxList([]);
    }
  }, [isEditMode]);

  const handleDescImageInsert = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("이미지 파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = document.createElement("img");
    img.src = blobUrl;
    img.style.maxWidth = "100%";
    img.style.height = "auto";

    const range =
      savedRangeRef.current ||
      (() => {
        const r = document.createRange();
        r.selectNodeContents(descEditorRef.current);
        r.collapse(false);
        return r;
      })();
    savedRangeRef.current = null;

    descEditorRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    setDescImgFiles((prev) => [...prev, { blobUrl, file }]);
  };

  const handleAttachFileChange = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (files.length === 0) return;

    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      alert("첨부파일 크기는 10MB를 초과할 수 없습니다.");
      return;
    }

    setAllAttachFiles((prev) => [
      ...prev,
      ...files.map((file) => ({ type: "new", data: file, name: file.name })),
    ]);
  };

  const handleAttachFileRemove = (idx) => {
    setAllAttachFiles((prev) => {
      const target = prev[idx];
      if (target?.type === "existing") {
        setDeletedAttachFileIdxList((ids) => [...ids, target.fileIdx]);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const handleSave = async () => {
    if (!editedNotice.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }

    const contentHtml = descEditorRef.current?.innerHTML || "";
    if (!contentHtml.trim() || contentHtml === "<br>") {
      alert("내용을 입력해주세요.");
      return;
    }

    setIsSaving(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("title", editedNotice.title);
      formDataObj.append("isPinned", editedNotice.isPinned);
      formDataObj.append("noticeType", editedNotice.noticeType);

      let processedHtml = contentHtml;
      descImgFiles.forEach(({ blobUrl }, idx) => {
        processedHtml = processedHtml
          .split(blobUrl)
          .join(`__PENDING_IMG_${idx}__`);
      });
      formDataObj.append("content", processedHtml);

      descImgFiles.forEach(({ file }) => {
        formDataObj.append("noticeImgs", file);
      });

      allAttachFiles
        .filter((attach) => attach.type === "new")
        .forEach((attach) => formDataObj.append("noticeFiles", attach.data));

      deletedAttachFileIdxList.forEach((fileIdx) =>
        formDataObj.append("deleteFileIdxList", fileIdx),
      );

      const response = await apiPutFormData(
        `/admin/notice/${noticeIdx}`,
        formDataObj,
      );

      if (response.ok) {
        alert("공지사항이 수정되었습니다.");
        setIsEditMode(false);
        if (onUpdate) onUpdate();

        const updatedResult = await getNoticeDetail(noticeIdx);
        if (updatedResult.success) {
          setNotice(updatedResult.data);
          setEditedNotice({
            title: updatedResult.data.title,
            isPinned: updatedResult.data.isPinned,
            noticeType: updatedResult.data.noticeType,
          });
        }
      } else {
        alert("공지사항 수정에 실패했습니다.");
      }
    } catch (err) {
      console.error("수정 실패:", err);
      alert("공지사항 수정 중 오류가 발생했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (notice) {
      setEditedNotice({
        title: notice.title,
        isPinned: notice.isPinned,
        noticeType: notice.noticeType,
      });
    }
    setDescImgFiles([]);
    setAllAttachFiles(
      (notice?.img?.files || []).map((f) => ({
        type: "existing",
        fileIdx: f.fileIdx,
        name: f.originalName,
        url: f.fileUrl,
      })),
    );
    setDeletedAttachFileIdxList([]);
    setIsEditMode(false);
  };

  if (loading) {
    return (
      <div className="adminnoticedetail-backdrop" onClick={onClose}>
        <div
          className="adminnoticedetail-wrap"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="adminnoticedetail-header">
            <h2 className="adminnoticedetail-title">공지사항 상세</h2>
            <button className="adminnoticedetail-close-btn" onClick={onClose}>
              <IoClose />
            </button>
          </div>
          <div className="adminnoticedetail-content">
            <p>로딩 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!notice) {
    return (
      <div className="adminnoticedetail-backdrop" onClick={onClose}>
        <div
          className="adminnoticedetail-wrap"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="adminnoticedetail-header">
            <h2 className="adminnoticedetail-title">공지사항 상세</h2>
            <button className="adminnoticedetail-close-btn" onClick={onClose}>
              <IoClose />
            </button>
          </div>
          <div className="adminnoticedetail-content">
            <p>공지사항 정보를 불러올 수 없습니다.</p>
          </div>
          <div className="adminnoticedetail-footer">
            <button
              className="adminnoticedetail-btn adminnoticedetail-btn-close"
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="adminnoticedetail-backdrop" onClick={onClose}>
      <div
        className="adminnoticedetail-wrap"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="adminnoticedetail-header">
          <h2 className="adminnoticedetail-title">공지사항 상세</h2>
          <button className="adminnoticedetail-close-btn" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        <div className="adminnoticedetail-content">
          {/* 제목 & 고정여부 */}
          <div className="adminnoticedetail-row">
            <label className="adminnoticedetail-label">제목</label>
            {isEditMode ? (
              <>
                <input
                  type="text"
                  className="adminnoticedetail-input"
                  value={editedNotice.title}
                  onChange={(e) =>
                    setEditedNotice({ ...editedNotice, title: e.target.value })
                  }
                />
                <label className="adminnoticedetail-checkbox-label">
                  <input
                    type="checkbox"
                    className="adminnoticedetail-checkbox"
                    checked={editedNotice.isPinned}
                    onChange={(e) =>
                      setEditedNotice({
                        ...editedNotice,
                        isPinned: e.target.checked,
                      })
                    }
                  />
                  <span>고정</span>
                </label>
              </>
            ) : (
              <div className="adminnoticedetail-value adminnoticedetail-value-with-badge">
                <span className="adminnoticedetail-title-text">
                  {notice.title}
                </span>
                {notice.isPinned ? (
                  <span className="adminnoticedetail-status-pinned">고정</span>
                ) : (
                  <span className="adminnoticedetail-status-normal">일반</span>
                )}
              </div>
            )}
          </div>

          {/* 공지 구분 */}
          <div className="adminnoticedetail-row">
            <label className="adminnoticedetail-label">공지 구분</label>
            {isEditMode ? (
              <select
                className="adminnoticedetail-select"
                value={editedNotice.noticeType || "공통"}
                onChange={(e) =>
                  setEditedNotice({
                    ...editedNotice,
                    noticeType: e.target.value,
                  })
                }
              >
                <option value="공통">전체</option>
                <option value="사용자">사용자</option>
                <option value="가맹점">가맹점</option>
              </select>
            ) : (
              <div className="adminnoticedetail-value">
                {notice.noticeType === "공통" ? "전체" : notice.noticeType}
              </div>
            )}
          </div>

          {/* 작성자 & 작성일 */}
          <div className="adminnoticedetail-row">
            <label className="adminnoticedetail-label">작성자/작성일시</label>
            <div className="adminnoticedetail-value">
              {notice.regNm} / {formatDateTime(notice.regDate)}
            </div>
          </div>

          {/* 수정자 & 수정일 */}
          <div className="adminnoticedetail-row">
            <label className="adminnoticedetail-label">수정자/수정일시</label>
            <div className="adminnoticedetail-value">
              {notice.modNm || "-"} / {formatDateTime(notice.modDate)}
            </div>
          </div>

          {/* 첨부파일 */}
          {isEditMode ? (
            <div className="adminnoticedetail-row adminnoticedetail-row-full">
              <label className="adminnoticedetail-label">첨부파일</label>
              <div style={{ flex: 1 }}>
                <label className="adminnoticedetail-file-btn">
                  파일 추가
                  <input
                    type="file"
                    multiple
                    onChange={handleAttachFileChange}
                    style={{ display: "none" }}
                  />
                </label>
                {allAttachFiles.length > 0 && (
                  <ul className="adminnoticedetail-file-list">
                    {allAttachFiles.map((file, idx) => (
                      <li key={idx} className="adminnoticedetail-file-item">
                        <span>{file.name}</span>
                        <button
                          type="button"
                          className="adminnoticedetail-file-remove-btn"
                          onClick={() => handleAttachFileRemove(idx)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            notice.img?.files?.length > 0 && (
              <div className="adminnoticedetail-row adminnoticedetail-row-full">
                <label className="adminnoticedetail-label">첨부파일</label>
                <ul className="adminnoticedetail-file-list">
                  {notice.img.files.map((file, idx) => (
                    <li key={idx} className="adminnoticedetail-file-item">
                      <a
                        href={file.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        download={file.originalName}
                      >
                        {file.originalName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}

          {/* 내용 */}
          <div className="adminnoticedetail-notice-content">
          <div className="adminnoticedetail-notice-content-title">내용</div>
          {isEditMode ? (
            <div className="desc-editor-wrap">
              <div className="desc-editor-toolbar">
                <button
                  type="button"
                  className="desc-insert-image-btn"
                  onClick={() => {
                    const sel = window.getSelection();
                    if (
                      sel.rangeCount > 0 &&
                      descEditorRef.current.contains(sel.anchorNode)
                    ) {
                      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
                    }
                    descImgInputRef.current?.click();
                  }}
                >
                  📷 이미지 삽입
                </button>
                <input
                  ref={descImgInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={handleDescImageInsert}
                />
              </div>
              <div
                ref={descEditorRef}
                contentEditable={true}
                className="desc-editor-content"
                suppressContentEditableWarning={true}
                data-placeholder="내용을 입력하세요"
              />
            </div>
          ) : (
            <div
              className="adminnoticedetail-notice-content-body"
              dangerouslySetInnerHTML={{ __html: notice.content || "" }}
            />
          )}
          </div>
        </div>

        <div className="adminnoticedetail-footer">
          {isEditMode ? (
            <>
              <button
                className="adminnoticedetail-btn adminnoticedetail-btn-save"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "저장 중..." : "저장"}
              </button>
              <button
                className="adminnoticedetail-btn adminnoticedetail-btn-cancel"
                onClick={handleCancel}
                disabled={isSaving}
              >
                취소
              </button>
            </>
          ) : (
            <>
              <button
                className="adminnoticedetail-btn adminnoticedetail-btn-edit"
                onClick={() => setIsEditMode(true)}
              >
                수정
              </button>
              <button
                className="adminnoticedetail-btn adminnoticedetail-btn-close"
                onClick={onClose}
              >
                닫기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNoticeDetail;
